import React, { useState, useEffect } from 'react';
import { useAppKitAccount, useAppKitProvider, useAppKit } from '@reown/appkit/react';
import { useLocation } from 'react-router-dom';
import { Connection, PublicKey, Transaction, SystemProgram, Keypair } from '@solana/web3.js';
import { TOKEN_PROGRAM_ID, createInitializeMintInstruction, createMintToInstruction, createAssociatedTokenAccountInstruction, getAssociatedTokenAddress, createSetAuthorityInstruction, AuthorityType } from '@solana/spl-token';
import { Buffer } from 'buffer';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet';
import { PinataSDK } from 'pinata-web3';

// Define the Metaplex Token Metadata Program ID
const TOKEN_METADATA_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');
const GATEWAY_URL = "https://gateway.pinata.cloud/ipfs/";

const pinata = new PinataSDK({
  pinataJwt: import.meta.env.VITE_PINATA_API_KEY || 'your_pinata_api_key',
  pinataGateway: "https://gateway.pinata.cloud",
});

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const CreateTokenContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  background: #1c2526;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 0 20px rgba(0, 196, 180, 0.3);
  border: 1px solid rgba(0, 196, 180, 0.2);
  animation: ${fadeIn} 0.5s ease-out;
  color: #e5e5e5;
  font-family: 'Poppins', sans-serif;
  @media (max-width: 768px) {
    padding: 1.5rem;
    margin: 1rem;
    max-width: 100%;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StepCircle = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${props => (props.active ? '#00c4b4' : '#2c2c44')};
  color: ${props => (props.active ? '#ffffff' : '#a0a0a0')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 600;
  border: 2px solid ${props => (props.active ? '#00c4b4' : '#2a6a68')};
  @media (max-width: 768px) {
    width: 25px;
    height: 25px;
    font-size: 0.9rem;
  }
`;

const StepLine = styled.div`
  width: 50px;
  height: 4px;
  background: #2a6a68;
  @media (max-width: 768px) {
    width: 40px;
  }
`;

const StepTitle = styled.h2`
  font-size: 2rem;
  color: #00c4b4;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const FormGroupWrapper = styled.div`
  margin: 0 1rem;
  @media (max-width: 768px) {
    margin: 0 0.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 1rem;
  color: #e5e5e5;
  margin-bottom: 0.5rem;
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.71rem;
  background: #2c2c44;
  color: #e5e5e5;
  border: 1px solid #2a6a68;
  border-radius: 8px;
  font-size: 0.95rem;
  &:focus {
    border-color: #00c4b4;
    outline: none;
    box-shadow: 0 0 5px rgba(0, 196, 180, 0.5);
  }
  @media (max-width: 768px) {
    padding: 0.71rem;
    font-size: 0.9rem;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.71rem;
  background: #2c2c44;
  color: #e5e5e5;
  border: 1px solid #2a6a68;
  border-radius: 8px;
  font-size: 0.95rem;
  min-height: 100px;
  resize: vertical;
  &:focus {
    border-color: #00c4b4;
    outline: none;
    box-shadow: 0 0 5px rgba(0, 196, 180, 0.5);
  }
  @media (max-width: 768px) {
    padding: 0.71rem;
    font-size: 0.9rem;
    min-height: 80px;
  }
`;

const ToggleGroupWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 1rem;
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const ToggleGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
  max-width: 700px;
  @media (max-width: 768px) {
    gap: 0.75rem;
    flex-direction: column;
    max-width: 100%;
  }
`;

const ToggleContainer = styled.div`
  flex: 1;
  min-width: 0;
  background: #2c2c44;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid #2a6a68;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  @media (max-width: 768px) {
    padding: 0.5rem;
    width: 100%;
  }
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #e5e5e5;
  font-size: 1rem;
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Toggle = styled.input`
  accent-color: #00c4b4;
  width: 20px;
  height: 20px;
  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
  }
`;

const ToggleFee = styled.span`
  font-size: 0.9rem;
  color: #00c4b4;
  margin-left: 0.5rem;
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const AuthorityDescription = styled.p`
  font-size: 0.9rem;
  color: #b0b0b0;
  margin-top: 0.5rem;
  line-height: 1.4;
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const FeeInfo = styled.p`
  font-size: 1rem;
  color: #b0b0b0;
  margin-top: 0.5rem;
  text-align: center;
  font-weight: 600;
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const FeeDetails = styled.p`
  font-size: 0.9rem;
  color: #a0a0a0;
  margin-top: 0.3rem;
  text-align: center;
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const SocialInputs = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SocialInput = styled(Input)`
  background: #3a3a50;
  border: 1px solid #2a6a68;
  &:focus {
    border-color: #00c4b4;
    box-shadow: 0 0 5px rgba(0, 196, 180, 0.5);
  }
`;

const OutputSection = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  text-align: center;
`;

const OutputLabel = styled.p`
  font-size: 1.25rem;
  color: #00c4b4;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const TokenContractAddress = styled.p`
  font-size: 1rem;
  color: #e5e5e5;
  word-break: break-all;
  background: #2c2c44;
  padding: 0.5rem;
  border-radius: 5px;
  margin-bottom: 1rem;
`;

const MetadataLink = styled.a`
  display: inline-block;
  background: linear-gradient(90deg, #2a6a68, #00c4b4);
  color: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0.5rem;
  transition: background 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;
  &:hover {
    background: linear-gradient(90deg, #00c4b4, #2a6a68);
    transform: translateY(-2px);
    box-shadow: 0 0 10px rgba(0, 196, 180, 0.5);
  }
`;

const SolscanLink = styled.a`
  display: inline-block;
  background: linear-gradient(90deg, #2a6a68, #00c4b4);
  color: #ffffff;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  margin: 0.5rem;
  transition: background 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;
  &:hover {
    background: linear-gradient(90deg, #00c4b4, #2a6a68);
    transform: translateY(-2px);
    box-shadow: 0 0 10px rgba(0, 196, 180, 0.5);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-top: 1.5rem;
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ActionButton = styled.button`
  background: linear-gradient(90deg, #2a6a68, #00c4b4);
  color: #ffffff;
  padding: 0.75rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;
  &:hover {
    background: linear-gradient(90deg, #00c4b4, #2a6a68);
    transform: translateY(-2px);
    box-shadow: 0 0 10px rgba(0, 196, 180, 0.5);
  }
  &:disabled {
    background: #555;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.95rem;
    width: 100%;
  }
`;

const BackButton = styled(ActionButton)`
  background: #2c2c44;
  &:hover {
    background: #3a3a50;
  }
`;

const ErrorMessage = styled.p`
  color: #ff4d94;
  margin-top: 1rem;
  text-align: center;
  font-size: 0.9rem;
  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoadingModal = styled.div`
  background: #1c2526;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 0 20px rgba(0, 196, 180, 0.3);
  border: 1px solid rgba(0, 196, 180, 0.2);
  color: #e5e5e5;
  width: 400px;
  max-width: 90%;
  text-align: center;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #00c4b4;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto 1rem auto;
`;

const LoadingStep = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
  font-size: 1rem;
  color: ${props => (props.completed ? '#00c4b4' : '#b0b0b0')};
`;

const Checkmark = styled.span`
  color: #00c4b4;
  font-size: 1.2rem;
`;

function CreateToken() {
  const location = useLocation();
  const { address, isConnected } = useAppKitAccount({ namespace: 'solana' });
  const { walletProvider } = useAppKitProvider('solana');
  const { open } = useAppKit();
  const [step, setStep] = useState(1);
  const [tokenName, setTokenName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [image, setImage] = useState(null);
  const [decimals, setDecimals] = useState(9);
  const [supply, setSupply] = useState(1000000000);
  const [description, setDescription] = useState('');
  const [enableSocials, setEnableSocials] = useState(true);
  const [socialLinks, setSocialLinks] = useState({ telegram: '', twitter: '', website: '', discord: '' });
  const [creatorInfo, setCreatorInfo] = useState({ enabled: true, name: 'Token Club', website: 'tokenclub.fun' });
  const [authorities, setAuthorities] = useState({ mint: true, freeze: true, update: false });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSteps, setLoadingSteps] = useState({
    payment: { text: 'Processing Payment...', completed: false },
    imageUpload: { text: 'Uploading Image to IPFS...', completed: false },
    metadataUpload: { text: 'Uploading Metadata to IPFS...', completed: false },
    tokenCreation: { text: 'Creating Token...', completed: false },
  });
  const [tokenContractAddress, setTokenContractAddress] = useState('');
  const [metadataUri, setMetadataUri] = useState('');
  const [estimatedFees, setEstimatedFees] = useState({ networkFee: 0, rentFee: 0, totalFee: 0 });

  // Only render if the path is exactly '/create-token'
  if (location.pathname !== '/create-token') {
    return null;
  }

  const HELIUS_API_KEY = import.meta.env.VITE_HELIUS_API_KEY || 'your_helius_api_key';
  const RPC_URL = `https://mainnet.helius-rpc.com/?api-key=${HELIUS_API_KEY}`;
  const WS_URL = `wss://mainnet.helius-rpc.com/?api-key=${HELIUS_API_KEY}`;
  const PAYMENT_WALLET = "E4J5TUyeVdviCCpTiqiBvYbah4kctpVtdbLwg3oT926y";
  const PINATA_API_KEY = import.meta.env.VITE_PINATA_API_KEY || 'your_pinata_api_key';
  const BASE_FEE = 0.1;
  const FEATURE_FEE = 0.1;
  const MAX_IMAGE_SIZE = 500 * 1024 * 1024;
  const MINT_SIZE = 82;
  const METADATA_ACCOUNT_SIZE = 607;

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError('');
      }, 10000);
    }
  }, [error]);

  const calculateTotalFee = () => {
    let total = BASE_FEE;
    if (creatorInfo.enabled) total += FEATURE_FEE;
    if (authorities.mint) total += FEATURE_FEE;
    if (authorities.freeze) total += FEATURE_FEE;
    return total;
  };

  const estimateFees = async () => {
    try {
      const connection = new Connection(RPC_URL, {
        commitment: 'confirmed',
        wsEndpoint: WS_URL,
      });
      const totalFeeSol = calculateTotalFee();
      const estimatedTxFeeSol = 0.000005 * 2;
      const mintLamports = await connection.getMinimumBalanceForRentExemption(MINT_SIZE);
      const estimatedRentSol = mintLamports / 1e9;
      const metadataLamports = await connection.getMinimumBalanceForRentExemption(METADATA_ACCOUNT_SIZE);
      const metadataRentSol = metadataLamports / 1e9;
      setEstimatedFees({
        networkFee: estimatedTxFeeSol,
        rentFee: estimatedRentSol + metadataRentSol,
        totalFee: totalFeeSol + estimatedTxFeeSol + estimatedRentSol + metadataRentSol,
      });
    } catch (err) {
      console.error('Failed to estimate fees:', err);
      setEstimatedFees({ networkFee: 0, rentFee: 0, totalFee: calculateTotalFee() });
    }
  };

  useEffect(() => {
    if (step === 3) {
      estimateFees();
    }
  }, [step, creatorInfo.enabled, authorities.mint, authorities.freeze]);

  const checkBalance = async (connection) => {
    const totalFeeSol = calculateTotalFee();
    const balance = await connection.getBalance(new PublicKey(address));
    const balanceSol = balance / 1e9;
    const estimatedTxFeeSol = 0.000005 * 2;
    const mintLamports = await connection.getMinimumBalanceForRentExemption(MINT_SIZE);
    const metadataLamports = await connection.getMinimumBalanceForRentExemption(METADATA_ACCOUNT_SIZE);
    const estimatedRentSol = (mintLamports + metadataLamports) / 1e9;
    const requiredSol = totalFeeSol + estimatedTxFeeSol + estimatedRentSol;
    if (balanceSol < requiredSol) {
      throw new Error(`Insufficient SOL in your wallet to create the token. You need at least ${requiredSol.toFixed(4)} SOL (including payment, rent, and fees), but you have ${balanceSol.toFixed(4)} SOL. Please add more SOL to your wallet and try again.`);
    }
  };

  const uploadImageToPinata = async (file, tokenSymbol) => {
    if (!PINATA_API_KEY || PINATA_API_KEY === 'your_pinata_api_key') {
      throw new Error('Pinata API key is not set or invalid. Please set VITE_PINATA_API_KEY in your environment variables and ensure it is a valid JWT from Pinata.');
    }
    try {
      const res = await pinata.upload.file(file)
        .cidVersion(1)
        .addMetadata({
          name: `${tokenSymbol}-logo-${Date.now()}`,
        });
      return `${GATEWAY_URL}${res.IpfsHash}`;
    } catch (err) {
      if (err.status === 401) {
        throw new Error('Invalid Pinata API key. Please check VITE_PINATA_API_KEY in your environment variables.');
      } else if (err.code === 'ECONNABORTED') {
        throw new Error('Pinata upload timed out. Please check your network connection and try again.');
      } else if (err.code === 'ENOTFOUND' || err.code === 'ECONNREFUSED') {
        throw new Error('Failed to connect to Pinata servers. Please check your internet connection and try again.');
      }
      throw new Error(`Failed to upload image to Pinata: ${err.message}`);
    }
  };

  const uploadMetadataToPinata = async (metadata) => {
    if (!PINATA_API_KEY || PINATA_API_KEY === 'your_pinata_api_key') {
      throw new Error('Pinata API key is not set or invalid. Please set VITE_PINATA_API_KEY in your environment variables and ensure it is a valid JWT from Pinata.');
    }
    try {
      const res = await pinata.upload.json(metadata)
        .cidVersion(1)
        .addMetadata({
          name: `${metadata.symbol}-metadata-${Date.now()}`,
        });
      return { metadataUri: `${GATEWAY_URL}${res.IpfsHash}`, ipfsHash: res.IpfsHash };
    } catch (err) {
      if (err.status === 401) {
        throw new Error('Invalid Pinata API key. Please check VITE_PINATA_API_KEY in your environment variables.');
      } else if (err.code === 'ECONNABORTED') {
        throw new Error('Pinata metadata upload timed out. Please check your network connection and try again.');
      } else if (err.code === 'ENOTFOUND' || err.code === 'ECONNREFUSED') {
        throw new Error('Failed to connect to Pinata servers for metadata upload. Please check your internet connection and try again.');
      }
      throw new Error(`Failed to upload metadata to Pinata: ${err.message}`);
    }
  };

  const updateLoadingStep = (step, completed) => {
    setLoadingSteps(prev => ({
      ...prev,
      [step]: { ...prev[step], completed },
    }));
  };

  const createToken = async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));

    console.log('Wallet connection state:', { isConnected, address, walletProvider });

    if (!isConnected || !address || !walletProvider) {
      setError('Please connect your wallet to proceed. Ensure your wallet is set to Mainnet.');
      open();
      return;
    }

    setLoading(true);
    setError('');
    setTokenContractAddress('');
    setMetadataUri('');
    setLoadingSteps({
      payment: { text: 'Processing Payment...', completed: false },
      imageUpload: { text: 'Uploading Image to IPFS...', completed: false },
      metadataUpload: { text: 'Uploading Metadata to IPFS...', completed: false },
      tokenCreation: { text: 'Creating Token...', completed: false },
    });

    try {
      const connection = new Connection(RPC_URL, {
        commitment: 'confirmed',
        wsEndpoint: WS_URL,
      });
      const payer = new PublicKey(address);

      try {
        const version = await connection.getVersion();
        console.log('Solana node version:', version['solana-core']);
      } catch (err) {
        if (err.code === 'ECONNABORTED') {
          throw new Error('RPC request timed out. Please check your network connection and try again.');
        } else if (err.code === 'ENOTFOUND' || err.code === 'ECONNREFUSED') {
          throw new Error('Failed to connect to Solana RPC server. Please check your internet connection or RPC URL and try again.');
        }
        throw new Error(`Failed to connect to Solana network: ${err.message}`);
      }

      await checkBalance(connection);

      updateLoadingStep('payment', false);
      const totalFee = calculateTotalFee();
      const totalFeeLamports = Math.floor(totalFee * 1e9);

      // Instruction 1: Transfer payment
      const paymentTransaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey: payer,
          toPubkey: new PublicKey(PAYMENT_WALLET),
          lamports: totalFeeLamports,
        })
      );

      paymentTransaction.recentBlockhash = (await connection.getLatestBlockhash('confirmed')).blockhash;
      paymentTransaction.feePayer = payer;

      let paymentSignature;
      try {
        console.log('Signing and sending payment transaction with wallet provider...');
        paymentSignature = await walletProvider.signAndSendTransaction(paymentTransaction);
      } catch (signErr) {
        throw new Error(`Failed to sign and send payment transaction: ${signErr.message || signErr.toString()}`);
      }
      console.log('Payment transaction signature:', paymentSignature);

      const paymentConfirmation = await connection.confirmTransaction({
        signature: paymentSignature,
        blockhash: paymentTransaction.recentBlockhash,
        lastValidBlockHeight: (await connection.getLatestBlockhash('confirmed')).lastValidBlockHeight,
      }, 'finalized');

      if (paymentConfirmation.value.err) {
        throw new Error('Payment transaction failed: ' + JSON.stringify(paymentConfirmation.value.err));
      }
      updateLoadingStep('payment', true);

      let imageUri = '';
      if (image) {
        updateLoadingStep('imageUpload', false);
        const imageSize = image.size;
        if (imageSize > MAX_IMAGE_SIZE) {
          throw new Error(`Image size exceeds the 500 MB limit. Please upload an image smaller than 500 MB.`);
        }
        imageUri = await uploadImageToPinata(image, symbol);
        updateLoadingStep('imageUpload', true);
      } else {
        updateLoadingStep('imageUpload', true);
      }

      updateLoadingStep('metadataUpload', false);
      const metadata = {
        name: tokenName,
        symbol: symbol,
        description: description,
        image: imageUri || '',
        attributes: [],
        seller_fee_basis_points: 0,
        external_url: socialLinks.website || '',
        collection: creatorInfo.enabled ? { name: creatorInfo.name, family: "TokenClub" } : undefined,
        properties: {
          files: imageUri ? [{ uri: imageUri, type: 'image' }] : [],
          category: 'image',
          creators: [
            {
              address: address,
              share: 100,
            },
          ],
          website: socialLinks.website || '',
          twitter: socialLinks.twitter || '',
          telegram: socialLinks.telegram || '',
          discord: socialLinks.discord || '',
          creator: {
            name: creatorInfo.name,
            website: creatorInfo.website,
          },
          details: {
            decimals: decimals,
            totalSupply: supply,
            authorities: {
              freeze: authorities.freeze,
              mint: authorities.mint,
              update: authorities.update,
            },
          },
        },
      };
      const { metadataUri: metadataUriResult } = await uploadMetadataToPinata(metadata);
      setMetadataUri(metadataUriResult);
      updateLoadingStep('metadataUpload', true);

      updateLoadingStep('tokenCreation', false);
      const mintKeypair = Keypair.generate();
      const mint = mintKeypair.publicKey;
      const mintAuthority = payer;
      const freezeAuthority = payer;

      const associatedTokenAccount = await getAssociatedTokenAddress(
        mint,
        payer,
        false,
        TOKEN_PROGRAM_ID
      );

      // Compute the metadata PDA (Program Derived Address)
      const [metadataPDA] = await PublicKey.findProgramAddress(
        [
          Buffer.from('metadata'),
          TOKEN_METADATA_PROGRAM_ID.toBuffer(),
          mint.toBuffer(),
        ],
        TOKEN_METADATA_PROGRAM_ID
      );

      const mainTransaction = new Transaction();

      // Instruction 2: Create mint account
      mainTransaction.add(
        SystemProgram.createAccount({
          fromPubkey: payer,
          newAccountPubkey: mint,
          space: MINT_SIZE,
          lamports: await connection.getMinimumBalanceForRentExemption(MINT_SIZE),
          programId: TOKEN_PROGRAM_ID,
        })
      );

      // Instruction 3: Initialize mint
      mainTransaction.add(
        createInitializeMintInstruction(
          mint,
          decimals,
          mintAuthority,
          freezeAuthority,
          TOKEN_PROGRAM_ID
        )
      );

      // Instruction 4: Create associated token account
      mainTransaction.add(
        createAssociatedTokenAccountInstruction(
          payer,
          associatedTokenAccount,
          payer,
          mint,
          TOKEN_PROGRAM_ID
        )
      );

      // Instruction 5: Mint tokens
      mainTransaction.add(
        createMintToInstruction(
          mint,
          associatedTokenAccount,
          mintAuthority,
          BigInt(supply * Math.pow(10, decimals)),
          [],
          TOKEN_PROGRAM_ID
        )
      );

      // Instruction 6: Create metadata account (createMetadataAccountV3)
      // Validate string lengths before serialization
      const nameBytes = Buffer.from(tokenName);
      if (nameBytes.length > 32) {
        throw new Error(`Token name must be 32 bytes or less. Current length: ${nameBytes.length} bytes.`);
      }

      const symbolBytes = Buffer.from(symbol);
      if (symbolBytes.length > 10) {
        throw new Error(`Token symbol must be 10 bytes or less. Current length: ${symbolBytes.length} bytes.`);
      }

      const uriBytes = Buffer.from(metadataUriResult);
      if (uriBytes.length > 200) {
        throw new Error(`Metadata URI must be 200 bytes or less. Current length: ${uriBytes.length} bytes.`);
      }

      const nameLength = Buffer.alloc(4);
      nameLength.writeUInt32LE(nameBytes.length, 0);

      const symbolLength = Buffer.alloc(4);
      symbolLength.writeUInt32LE(symbolBytes.length, 0);

      const uriLength = Buffer.alloc(4);
      uriLength.writeUInt32LE(uriBytes.length, 0);

      const dataV2 = Buffer.concat([
        nameLength,
        nameBytes,
        symbolLength,
        symbolBytes,
        uriLength,
        uriBytes,
        Buffer.from(new Uint16Array([0]).buffer),
        Buffer.from([0]),
        Buffer.from([0]),
        Buffer.from([0]),
      ]);

      const metadataInstructionData = Buffer.concat([
        Buffer.from([33]),
        dataV2,
        Buffer.from([1]),
        Buffer.from([0]),
      ]);

      mainTransaction.add({
        keys: [
          { pubkey: metadataPDA, isSigner: false, isWritable: true },
          { pubkey: mint, isSigner: false, isWritable: false },
          { pubkey: payer, isSigner: true, isWritable: false },
          { pubkey: payer, isSigner: false, isWritable: true },
          { pubkey: payer, isSigner: false, isWritable: false },
          { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
          { pubkey: new PublicKey('SysvarRent111111111111111111111111111111111'), isSigner: false, isWritable: false },
        ],
        programId: TOKEN_METADATA_PROGRAM_ID,
        data: metadataInstructionData,
      });

      // Instruction 7: Revoke mint authority if enabled
      if (authorities.mint) {
        mainTransaction.add(
          createSetAuthorityInstruction(
            mint,
            payer,
            AuthorityType.MintTokens,
            null,
            [],
            TOKEN_PROGRAM_ID
          )
        );
      }

      if (authorities.freeze) {
        mainTransaction.add(
          createSetAuthorityInstruction(
            mint,
            payer,
            AuthorityType.FreezeAccount,
            null,
            [],
            TOKEN_PROGRAM_ID
          )
        );
      }

      mainTransaction.recentBlockhash = (await connection.getLatestBlockhash('confirmed')).blockhash;
      mainTransaction.feePayer = payer;
      mainTransaction.partialSign(mintKeypair);

      const mainSignature = await walletProvider.signAndSendTransaction(mainTransaction);
      await connection.confirmTransaction({
        signature: mainSignature,
        blockhash: mainTransaction.recentBlockhash,
        lastValidBlockHeight: (await connection.getLatestBlockhash('confirmed')).lastValidBlockHeight,
      }, 'finalized');
      updateLoadingStep('tokenCreation', true);

      setTokenContractAddress(mint.toBase58());
      setLoading(false);
    } catch (err) {
      console.error('Token creation error (raw error):', err);
      const errorMessage = typeof err === 'string' ? err : (err.message || 'An unknown error occurred');
      if (typeof errorMessage === 'string') {
        if (errorMessage.includes('timed out') || errorMessage.includes('timeout')) {
          setError('Transaction timed out. Please check your network connection and try again.');
        } else if (errorMessage.includes('connect') || errorMessage.includes('connection')) {
          setError('Network connection error. Please check your internet connection and try again.');
        } else {
          setError(`Error: ${errorMessage}`);
        }
      } else {
        setError('Error: An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNextStep = () => {
    if (step === 1) {
      const nameError = validateTokenName(tokenName);
      const symbolError = validateSymbol(symbol);
      if (nameError) {
        setError(nameError);
        return;
      }
      if (symbolError) {
        setError(symbolError);
        return;
      }
      if (!tokenName || !symbol) {
        setError('Please fill in all required fields.');
        return;
      }
    }
    if (step === 2 && (decimals < 0 || supply <= 0)) {
      setError('Please enter valid decimals and supply.');
      return;
    }
    if (step === 3) {
      if (enableSocials) {
        const urlFields = ['website', 'twitter', 'telegram', 'discord'];
        for (const field of urlFields) {
          const urlError = validateUrl(socialLinks[field]);
          if (urlError) {
            setError(urlError);
            return;
          }
        }
      }
      if (creatorInfo.enabled) {
        const creatorNameError = validateTokenName(creatorInfo.name);
        const creatorWebsiteError = validateUrl(creatorInfo.website);
        if (creatorNameError) {
          setError(creatorNameError);
          return;
        }
        if (creatorWebsiteError) {
          setError(creatorWebsiteError);
          return;
        }
      }
    }
    setError('');
    setStep(step + 1);
  };

  const handleSocialToggle = () => {
    setEnableSocials((prev) => !prev);
    if (enableSocials) {
      setSocialLinks({ telegram: '', twitter: '', website: '', discord: '' });
    }
  };

  const validateTokenName = (name) => {
    const maxLength = 32;
    const validNameRegex = /^[a-zA-Z0-9\s]+$/;
    if (!name || name.length > maxLength) return `Token name must be 1-${maxLength} characters.`;
    if (!validNameRegex.test(name)) return 'Token name can only contain letters, numbers, and spaces.';
    return null;
  };

  const validateSymbol = (symbol) => {
    const maxLength = 10;
    const validSymbolRegex = /^[a-zA-Z0-9]+$/;
    if (!symbol || symbol.length > maxLength) return `Token symbol must be 1-${maxLength} characters.`;
    if (!validSymbolRegex.test(symbol)) return 'Token symbol can only contain letters and numbers.';
    return null;
  };

  const validateUrl = (url) => {
    if (!url) return null;
    const maxLength = 200;
    const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
    if (url.length > maxLength) return `URL must be less than ${maxLength} characters.`;
    if (!urlRegex.test(url)) return 'Please enter a valid URL.';
    return null;
  };

  return (
    <CreateTokenContainer>
      <Helmet>
        <title>Create Token - TokenClub</title>
        <meta name="description" content="Create your own Solana token in three easy steps with TokenClub. Add socials, set authorities, and launch your token." />
        <meta name="keywords" content="create token Solana, Solana token maker, token creator, meme coin maker" />
      </Helmet>
      <StepIndicator>
        <StepCircle active={step === 1}>1</StepCircle>
        <StepLine />
        <StepCircle active={step === 2}>2</StepCircle>
        <StepLine />
        <StepCircle active={step === 3}>3</StepCircle>
      </StepIndicator>
      {step === 1 && (
        <>
          <StepTitle>Create Your Own SPL Token</StepTitle>
          <FormGroupWrapper>
            <FormGroup>
              <Label>Token Name *</Label>
              <Input value={tokenName} onChange={(e) => setTokenName(e.target.value)} placeholder="Enter token name" />
            </FormGroup>
            <FormGroup>
              <Label>Symbol *</Label>
              <Input value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="Enter token symbol" />
            </FormGroup>
            <FormGroup>
              <Label>Token Image (Max 500x500 px)</Label>
              <Input type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} />
            </FormGroup>
          </FormGroupWrapper>
          <ButtonGroup>
            <ActionButton onClick={handleNextStep}>Next</ActionButton>
          </ButtonGroup>
        </>
      )}
      {step === 2 && (
        <>
          <StepTitle>Create Your Own SPL Token</StepTitle>
          <FormGroupWrapper>
            <FormGroup>
              <Label>Decimals (Recommended: 9)</Label>
              <Input type="number" value={decimals} onChange={(e) => setDecimals(Number(e.target.value))} placeholder="Enter value between 0 and 15 decimals" />
            </FormGroup>
            <FormGroup>
              <Label>Total Supply (Recommended: 1B)</Label>
              <Input type="number" value={supply} onChange={(e) => setSupply(Number(e.target.value))} placeholder="Common supply: 1,000,000,000" />
            </FormGroup>
            <FormGroup>
              <Label>Description</Label>
              <TextArea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your token's purpose and vision..."
              />
            </FormGroup>
          </FormGroupWrapper>
          <ButtonGroup>
            <BackButton onClick={() => setStep(step - 1)}>Back</BackButton>
            <ActionButton onClick={handleNextStep}>Next</ActionButton>
          </ButtonGroup>
        </>
      )}
      {step === 3 && (
        <>
          <StepTitle>Create Your Own SPL Token</StepTitle>
          <FormGroupWrapper>
            <FormGroup>
              <Label>Token Socials</Label>
              {enableSocials && (
                <SocialInputs>
                  <SocialInput
                    value={socialLinks.website}
                    onChange={(e) => setSocialLinks({ ...socialLinks, website: e.target.value })}
                    placeholder="Website (e.g., https://yourmemecoin.fun)"
                  />
                  <SocialInput
                    value={socialLinks.twitter}
                    onChange={(e) => setSocialLinks({ ...socialLinks, twitter: e.target.value })}
                    placeholder="Twitter (e.g., https://twitter.com/yourmemecoin)"
                  />
                  <SocialInput
                    value={socialLinks.telegram}
                    onChange={(e) => setSocialLinks({ ...socialLinks, telegram: e.target.value })}
                    placeholder="Telegram (e.g., https://t.me/yourchannel)"
                  />
                  <SocialInput
                    value={socialLinks.discord}
                    onChange={(e) => setSocialLinks({ ...socialLinks, discord: e.target.value })}
                    placeholder="Discord (e.g., https://discord.gg/your-server)"
                  />
                </SocialInputs>
              )}
              <ToggleLabel>
                <Toggle type="checkbox" checked={enableSocials} onChange={handleSocialToggle} />
                Enable Token Socials
              </ToggleLabel>
            </FormGroup>
            <FormGroup>
              <Label>Modify Creator Information <ToggleFee>(0.1 SOL)</ToggleFee></Label>
              <ToggleLabel>
                <Toggle type="checkbox" checked={creatorInfo.enabled} onChange={() => setCreatorInfo({ ...creatorInfo, enabled: !creatorInfo.enabled })} />
                Enable Creator Info
              </ToggleLabel>
              {creatorInfo.enabled && (
                <>
                  <Input value={creatorInfo.name} onChange={(e) => setCreatorInfo({ ...creatorInfo, name: e.target.value })} placeholder="Creator Name" />
                  <Input value={creatorInfo.website} onChange={(e) => setCreatorInfo({ ...creatorInfo, website: e.target.value })} placeholder="Creator Website (e.g., https://coinfast.fun)" />
                </>
              )}
            </FormGroup>
          </FormGroupWrapper>
          <FormGroup>
            <Label>Revoke Authorities (Selected by Default)</Label>
            <AuthorityDescription>
              Solana Token has 2 authorities: Freeze Authority and Mint Authority. Revoke them to attract more investors. We highly recommend revoking these options for gaining more trust. *Both options are pre-selected by default to turn them off.
            </AuthorityDescription>
            <ToggleGroupWrapper>
              <ToggleGroup>
                <ToggleContainer>
                  <ToggleLabel>
                    <Toggle type="checkbox" checked={authorities.freeze} onChange={() => setAuthorities({ ...authorities, freeze: !authorities.freeze })} />
                    Revoke Freeze Authority <ToggleFee>(0.1 SOL)</ToggleFee>
                  </ToggleLabel>
                  <AuthorityDescription>Freeze Authority allows you to freeze token accounts of holders.</AuthorityDescription>
                </ToggleContainer>
                <ToggleContainer>
                  <ToggleLabel>
                    <Toggle type="checkbox" checked={authorities.mint} onChange={() => setAuthorities({ ...authorities, mint: !authorities.mint })} />
                    Revoke Mint Authority <ToggleFee>(0.1 SOL)</ToggleFee>
                  </ToggleLabel>
                  <AuthorityDescription>Mint Authority allows you to mint more supply of your token.</AuthorityDescription>
                </ToggleContainer>
              </ToggleGroup>
            </ToggleGroupWrapper>
          </FormGroup>
          <FeeInfo>Cost: {calculateTotalFee().toFixed(1)} SOL</FeeInfo>
          <FeeDetails>
            Estimated Network Fee: {estimatedFees.networkFee.toFixed(6)} SOL | Rent Fee: {estimatedFees.rentFee.toFixed(6)} SOL | Total Estimated: {estimatedFees.totalFee.toFixed(6)} SOL
          </FeeDetails>
          <ButtonGroup>
            <BackButton onClick={() => setStep(step - 1)}>Back</BackButton>
            <ActionButton onClick={createToken} disabled={loading}>
              {loading ? 'Creating Token...' : 'Create Token'}
            </ActionButton>
          </ButtonGroup>
          {tokenContractAddress && (
            <OutputSection>
              <OutputLabel>Token Created Successfully!</OutputLabel>
              <TokenContractAddress>Token Contract Address: {tokenContractAddress}</TokenContractAddress>
              <SolscanLink href={`https://solscan.io/token/${tokenContractAddress}?cluster=mainnet`} target="_blank" rel="noopener noreferrer">
                Check Your Token on Solscan
              </SolscanLink>
              {metadataUri && (
                <MetadataLink href={metadataUri} target="_blank" rel="noopener noreferrer">
                  View Metadata on Pinata
                </MetadataLink>
              )}
            </OutputSection>
          )}
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </>
      )}
      {loading && (
        <LoadingOverlay>
          <LoadingModal>
            <Spinner />
            <h3>Creating Your Token...</h3>
            <LoadingStep completed={loadingSteps.payment.completed}>
              {loadingSteps.payment.completed ? <Checkmark>✔</Checkmark> : <span>⏳</span>}
              {loadingSteps.payment.text}
            </LoadingStep>
            <LoadingStep completed={loadingSteps.imageUpload.completed}>
              {loadingSteps.imageUpload.completed ? <Checkmark>✔</Checkmark> : <span>⏳</span>}
              {loadingSteps.imageUpload.text}
            </LoadingStep>
            <LoadingStep completed={loadingSteps.metadataUpload.completed}>
              {loadingSteps.metadataUpload.completed ? <Checkmark>✔</Checkmark> : <span>⏳</span>}
              {loadingSteps.metadataUpload.text}
            </LoadingStep>
            <LoadingStep completed={loadingSteps.tokenCreation.completed}>
              {loadingSteps.tokenCreation.completed ? <Checkmark>✔</Checkmark> : <span>⏳</span>}
              {loadingSteps.tokenCreation.text}
            </LoadingStep>
          </LoadingModal>
        </LoadingOverlay>
      )}
    </CreateTokenContainer>
  );
}

export default CreateToken;